import * as Comp from './coachmark-content.styles'
import { useWindowDimensions } from 'react-native'
import { CoachmarkHeader } from './components/coachmark-header/index.tsx'
import { CoachMarkFooter } from './components/coachmark-footer/index.tsx'
import { useState } from 'react'
import { layoutUtils } from '~/utils/layout/layout-utils'
import type { LayoutChangeEvent, ViewProps } from 'react-native'
import {
  useTheme,
  type LayoutInfo,
  useFloatingPosition,
} from '~/hooks/index.ts'
import {
  PositionAlignment,
  PositionDirection,
} from '~/utils/layout/position-enums'
import { CoachmarkArrow } from './components/coachmark-arrow/index.tsx'

interface CoachmarkContentProps extends ViewProps {
  isOpen?: boolean
  title?: string
  description?: string
  totalStep?: number
  currentStep?: number
  onPrevious?: () => void
  onNext?: () => void
  onClose?: () => void
  side?: PositionDirection
  align?: PositionAlignment
  autoPosition?: boolean
  rootLayout?: LayoutInfo
  tag?: React.ReactNode
  rootLayoutReady?: boolean
}

export function CoachmarkContent({
  isOpen = false,
  title = '',
  description = '',
  totalStep = 1,
  currentStep = 1,
  onPrevious = () => {},
  onNext = () => {},
  onClose = () => {},
  side = PositionDirection.TOP,
  align = PositionAlignment.MIDDLE,
  autoPosition = true,
  rootLayout,
  tag,
  rootLayoutReady = false,
  children,
  ...viewProps
}: CoachmarkContentProps) {
  const { width: screenWidth } = useWindowDimensions()
  const theme = useTheme()

  const SCREEN_MARGIN = theme.spacingInlineFourpulse
  const CONTENT_GAP = theme.spacingInlineTwopulse
  const HALF = 2

  const [contentLayout, setContentLayout] = useState<LayoutInfo>()

  const { side: finalSide, styles: positionStyles } = useFloatingPosition({
    side,
    align,
    autoPosition,
    rootLayout: rootLayout || null,
    contentLayout: contentLayout || null,
    gap: CONTENT_GAP,
    screenPadding: SCREEN_MARGIN / HALF,
  })

  const handleLayout = (event: LayoutChangeEvent) => {
    const { x, y, width, height } = event.nativeEvent.layout
    const newLayout = { x, y, width, height }

    if (layoutUtils.hasLayoutChanged(contentLayout, newLayout)) {
      setContentLayout(newLayout)
    }
  }

  const isReady = rootLayoutReady && !!contentLayout

  const calculateMaxWidth = () => {
    const defaultMaxWidth = theme.sizingMediumlarge * theme.sizingMini
    const availableWidth = screenWidth - SCREEN_MARGIN * 2

    return Math.min(defaultMaxWidth, availableWidth)
  }

  if (!isOpen) return null

  return (
    <Comp.Wrapper
      style={[
        {
          width: screenWidth - SCREEN_MARGIN,
          alignItems: 'center',
          height: (rootLayout?.height || 0) + (contentLayout?.height || 0),
          left: -(rootLayout?.x || 0) + SCREEN_MARGIN / HALF,
          opacity: isReady ? 1 : 0,
        },
        positionStyles.wrapper,
      ]}
      pointerEvents="box-none"
    >
      <CoachmarkArrow side={finalSide} rootLayout={rootLayout} />
      <Comp.Container
        style={{
          maxWidth: calculateMaxWidth(),
          ...positionStyles.container,
        }}
        onLayout={handleLayout}
        {...viewProps}
      >
        <CoachmarkHeader
          title={title}
          tag={tag}
          onClose={onClose}
          testID="coachmark-content-header"
        />
        {description && (
          <Comp.Description numberOfLines={3}>{description}</Comp.Description>
        )}
        {children && <Comp.Slot>{children}</Comp.Slot>}
        <CoachMarkFooter
          currentStep={currentStep}
          totalStep={totalStep}
          onPrevious={onPrevious}
          onNext={onNext}
          testID="coachmark-content-footer"
        />
      </Comp.Container>
    </Comp.Wrapper>
  )
}
